import React from "react";
import { Check, Plus, Shield } from "lucide-react";
import { SITE } from "@/data/site";
import { czk, bundleFullPrice, bundleSavings } from "@/lib/price";

const tile = (on) =>
  `group relative text-left flex items-start gap-3 p-3.5 border transition-all duration-200 ${
    on ? "border-brand-accent bg-brand-accent/10" : "border-brand-border bg-brand-bg hover:border-white/40"
  }`;

const box = (on) =>
  `mt-0.5 w-5 h-5 shrink-0 border flex items-center justify-center transition-colors ${
    on ? "bg-brand-accent border-brand-accent text-black" : "border-brand-border text-transparent group-hover:text-brand-muted"
  }`;

const Ribbon = ({ children }) => (
  <span className="absolute -top-2 right-3 bg-brand-accent text-black text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5">
    {children}
  </span>
);

export function AddonPicker({ selected, bundles, onToggle, onToggleBundle }) {
  const covered = SITE.bundles.filter((b) => bundles.includes(b.key)).flatMap((b) => b.addons);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-muted">
          Doplňky <span className="text-white/50">(volitelné)</span>
        </div>
        <div className="text-xs text-brand-accent">Většina zákazníků přidává 1–2</div>
      </div>

      {/* One-click bundles */}
      <div className="grid sm:grid-cols-2 gap-2" data-testid="bundle-list">
        {SITE.bundles.map((b) => {
          const on = bundles.includes(b.key);
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => onToggleBundle(b.key)}
              aria-pressed={on}
              data-testid={`bundle-${b.key}`}
              className={`${tile(on)} ${on ? "" : "border-brand-accent/40"}`}
            >
              <span className={box(on)}>{on ? <Check size={13} strokeWidth={3} /> : <Plus size={13} />}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-white truncate">
                    <Shield size={13} className="text-brand-accent shrink-0" />
                    {b.label}
                  </span>
                  <span className={`text-sm font-display font-black shrink-0 ${on ? "text-brand-accent" : "text-white"}`}>
                    +{czk(b.price)}
                  </span>
                </span>
                <span className="block text-xs text-brand-muted mt-0.5 leading-snug">{b.desc}</span>
                <span className="block text-[11px] mt-1">
                  <span className="text-brand-muted line-through">{czk(bundleFullPrice(b))}</span>{" "}
                  <span className="text-brand-accent">ušetříte {czk(bundleSavings(b))}</span>
                </span>
              </span>
              <Ribbon>{b.tag}</Ribbon>
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 gap-2" data-testid="addon-list">
        {SITE.addons.map((a) => {
          const inBundle = covered.includes(a.key);
          const on = inBundle || selected.includes(a.key);
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => onToggle(a.key)}
              aria-pressed={on}
              data-testid={`addon-${a.key}`}
              data-in-bundle={inBundle || undefined}
              className={tile(on)}
            >
              <span className={box(on)}>{on ? <Check size={13} strokeWidth={3} /> : <Plus size={13} />}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white truncate">{a.label}</span>
                  <span className={`text-sm font-display font-black shrink-0 ${on ? "text-brand-accent" : "text-white/80"}`}>
                    {inBundle ? "v balíčku" : `+${czk(a.price)}`}
                  </span>
                </span>
                <span className="block text-xs text-brand-muted mt-0.5 leading-snug">{a.desc}</span>
              </span>
              {a.popular && !inBundle && <Ribbon>Nejoblíbenější</Ribbon>}
              {a.premium && !inBundle && <Ribbon>Premium</Ribbon>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
